'use client';

import { useWishList } from '@/features/account/api/query/wishlist';
import { useLogout } from '@/features/account/hooks/logout';
import { useLoginUserMutation } from '@/features/basket/api/mutation/login-user';
import type { LoginInputs } from '@/features/basket/components/login/log-in-with-email';
import { useUpdateMarketingConsentsAfterLogin } from '@/features/login/hooks/use-update-marketing-consents-after-login';
import { addAuthTokenParams } from '@/features/login/utils/add-auth-token-params';
import {
  useCookies,
  useManageCookies,
  useSetCookie,
} from '@/hooks/use-cookies';
import { useRouter } from '@/navigation';
import { Cookie, type CustomerType, SessionStorage } from '@/types/storage';
import { COOKIES_EXPIRATION_DATE } from '@/utils/constants';
import { type JwtPayload, jwtDecode } from 'jwt-decode';
import { useEffect, useTransition } from 'react';
import { isJWT } from 'validator';

export interface JWTToken extends JwtPayload {
  data: { email: string; id: string; user_id: string };
  exp: number;
  iat: number;
}

type LoginSuccessCallback = (token: string) => void;

type LoginSocialMediaData = {
  token: string;
  customerType: CustomerType;
  onSuccess?: LoginSuccessCallback;
};

export const useCustomer = () => ({
  email: useCookies(Cookie.CUSTOMER_EMAIL),
  token: useCookies(Cookie.LOGIN_SECRET),
  customerType: useCookies(Cookie.CUSTOMER_TYPE),
});

export const useLoginAndStoreData = (redirect: string) => {
  const { mutateAsync: loginUser, ...rest } = useLoginUserMutation();
  const [isTransitionPending, startTransition] = useTransition();
  const { refetch } = useWishList();
  const router = useRouter();
  const setCookie = useSetCookie();

  const { updateMarketingConsents } = useUpdateMarketingConsentsAfterLogin();

  const handleLogin = (
    token: string,
    customerType: CustomerType,
    email: string,
  ) => {
    setCookie(Cookie.CUSTOMER_EMAIL, email, {
      expires: COOKIES_EXPIRATION_DATE,
    });
    setCookie(Cookie.LOGIN_SECRET, token, {
      expires: COOKIES_EXPIRATION_DATE,
    });
    setCookie(Cookie.JUST_LOGGED_IN, true, {
      expires: COOKIES_EXPIRATION_DATE,
    });
    setCookie(Cookie.CUSTOMER_TYPE, customerType, {
      expires: COOKIES_EXPIRATION_DATE,
    });
  };

  const loginUserAndStoreData = async (
    data: LoginInputs,
    onSuccess?: LoginSuccessCallback,
  ) => {
    try {
      const { token, customerType } = await loginUser(
        data as Required<LoginInputs>,
      );
      if (!isJWT(token)) {
        return { success: false };
      }
      const {
        data: { email },
      } = jwtDecode<JWTToken>(token);
      handleLogin(token, customerType, email);
      refetch();
      sessionStorage.removeItem(SessionStorage.ON_SUCCESSFULLY_LOGIN);

      try {
        await updateMarketingConsents(email);
      } catch {
        // marketing consents update is non-critical
      }

      startTransition(() => {
        if (onSuccess) {
          onSuccess(token);
        } else {
          const urlWithAuthToken = addAuthTokenParams(redirect, token);
          router.push(urlWithAuthToken);
        }
      });

      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const loginUserWithSocialMedia = async ({
    token,
    customerType,
    onSuccess,
  }: LoginSocialMediaData) => {
    const {
      data: { email },
    } = jwtDecode<JWTToken>(token);
    handleLogin(token, customerType, email);
    await updateMarketingConsents(email);

    if (onSuccess) {
      onSuccess(token);
    }
    const urlWithAuthToken = addAuthTokenParams(redirect, token);
    startTransition(() => {
      router.push(urlWithAuthToken);
    });
  };

  return {
    loginUserAndStoreData,
    loginUserWithSocialMedia,
    isTransitionPending,
    ...rest,
  };
};

export const useVerifyLoginStateEffect = () => {
  const { token, customerType } = useCustomer();
  const [setCookie, removeCookie] = useManageCookies();
  const { logout: handleLogout } = useLogout();
  const logout = () => {
    removeCookie(Cookie.LOGIN_SECRET);
    removeCookie(Cookie.JUST_LOGGED_IN);
    removeCookie(Cookie.CUSTOMER_TYPE);
  };

  useEffect(() => {
    (async () => {
      if (!token) {
        return logout();
      }
      const {
        exp,
        data: { email },
      } = jwtDecode<JWTToken>(token);

      const remainingAge = exp - Math.floor(Date.now() / 1000);

      if (remainingAge <= 0) {
        return handleLogout();
      }

      setCookie(Cookie.CUSTOMER_EMAIL, email, {
        expires: COOKIES_EXPIRATION_DATE,
      });
      setCookie(Cookie.CUSTOMER_TYPE, customerType ?? 'new', {
        expires: COOKIES_EXPIRATION_DATE,
      });
      setCookie(Cookie.LOGIN_SECRET, token, {
        maxAge: remainingAge,
      });
    })();
  }, [token]);
};
